import { z } from 'zod';
import { sortParam } from '../sort.js';
import { CONTACT_KINDS } from '../contacts/contacts.js';
import { updateContactFinanceInput } from './settings.js';
/**
 * Debitoren und Kreditoren (KON-161). Ein Debitor ist ein Kunde, ein Kreditor ein Lieferant, der ein Personenkonto in der Buchhaltung hat. Jede Art hat eine eigene
 * Tabelle, Liste und Maske. Beide verweisen auf einen Kontakt, der auch in Kontakte erscheint. Eine Firma kann Kunde und Lieferant zugleich sein und steht dann in beiden.
 * Zahlungsbedingungen, USt-IdNr. und Bankverbindung stehen als Finanzdaten am Kontakt.
 */
export const ACCOUNT_KINDS = ['debtor', 'creditor'];
export const ACCOUNT_LABELS = {
    debtor: { singular: 'Debitor', plural: 'Debitoren', person: 'Kunde', number: 'Debitorennummer' },
    creditor: { singular: 'Kreditor', plural: 'Kreditoren', person: 'Lieferant', number: 'Kreditorennummer' },
};
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
/** Neuer Kontakt, der gleich Debitor oder Kreditor wird. Die Finanzdaten sind freiwillig. */
export const newAccountContactInput = z
    .object({
    kind: z.enum(CONTACT_KINDS, 'Bitte Person oder Firma wählen').default('company'),
    name: z.string().trim().min(1, 'Name fehlt').max(120),
    email: text(200),
    phone: text(40),
    street: text(120),
    postalCode: text(10),
    city: text(80),
})
    .strict();
/**
 * Konto anlegen: entweder für einen vorhandenen Kontakt (`contactId`) oder mit einem neuen (`contact`). Ein Kontakt hat je Art höchstens ein Konto.
 * `finance` übernimmt zum Beispiel USt-IdNr. und IBAN aus einer Rechnung gleich in die Finanzdaten des Kontakts.
 */
export const createAccountInput = z
    .object({
    contactId: z.uuid('Ungültiger Kontakt').optional(),
    contact: newAccountContactInput.optional(),
    finance: updateContactFinanceInput.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.contactId === undefined) === (v.contact === undefined))
        ctx.addIssue({ code: 'custom', path: ['contactId'], message: 'Bitte einen Kontakt wählen oder einen neuen angeben' });
    if (v.contactId && v.finance)
        ctx.addIssue({ code: 'custom', path: ['finance'], message: 'Finanzdaten eines vorhandenen Kontakts ändern Sie in dessen Finanzdaten' });
});
/** Ein Konto lässt sich sperren und mit einer Notiz versehen. Nummer und Kontakt ändern sich nie. */
export const updateAccountInput = z
    .object({
    active: z.boolean().optional(),
    note: z.string().trim().max(2000).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const ACCOUNT_ACTIVE_FILTERS = ['active', 'inactive', 'all'];
export const listAccountsQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(100).optional(),
    status: z.enum(ACCOUNT_ACTIVE_FILTERS).default('active'),
    sort: sortParam(['number', 'name', 'city', 'createdAt'], 'number').optional(),
});
//# sourceMappingURL=accounts.js.map